import { request } from '../base';
import type { ApiResponse, PaginatedResponse, PaginationParams } from '../base';

// ==========================================
// Phase 49: Batch Recall Management
// ==========================================

// Recall Events (RM prefix to avoid conflict with genealogy recalls)
export async function getRMRecallEvents(params?: PaginationParams & { status?: string; recallType?: string; recallClass?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.recallType) searchParams.set('recallType', params.recallType);
  if (params?.recallClass) searchParams.set('recallClass', params.recallClass);
  return request<PaginatedResponse<any>>(`/recalls/events?${searchParams}`);
}

export async function getRMRecallEvent(id: string) {
  return request<any>(`/recalls/events/${id}`);
}

export async function createRMRecallEvent(data: {
  recallTitle: string;
  recallType: string;
  recallClass?: string;
  initiatorType: string;
  initiationDate: string;
  recallReason: string;
  hazardDescription?: string;
  healthRiskLevel?: string;
  affectedProductTypes?: string[];
  geographicScope?: string;
  recommendedAction?: string;
  customerInstructions?: string;
  recallCoordinatorId?: string;
  recallTeam?: any[];
}) {
  return request<any>('/recalls/events', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRMRecallEvent(id: string, data: Partial<{
  recallStatus: string;
  notificationDate: string;
  completionDate: string;
  rootCause: string;
  rootCauseCategory: string;
  effectivenessResult: string;
  recoveryRate: string;
  notes: string;
}>) {
  return request<any>(`/recalls/events/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Recalled Batches
export async function getRecalledBatches(params?: PaginationParams & { recallEventId?: string; recoveryStatus?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.recallEventId) searchParams.set('recallEventId', params.recallEventId);
  if (params?.recoveryStatus) searchParams.set('recoveryStatus', params.recoveryStatus);
  return request<PaginatedResponse<any>>(`/recalls/batches?${searchParams}`);
}

export async function createRecalledBatch(data: {
  recallEventId: string;
  batchId?: string;
  batchNumber: string;
  lotNumber?: string;
  entityType: string;
  entityId: string;
  entityName?: string;
  totalQuantity?: string;
  quantityUnit?: string;
  quantityDistributed?: string;
  quantityInStock?: string;
}) {
  return request<any>('/recalls/batches', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRecalledBatch(id: string, data: Partial<{
  recoveryStatus: string;
  quantityRecovered: string;
  quantityDestroyed: string;
  holdStatus: string;
  traceComplete: boolean;
  notes: string;
}>) {
  return request<any>(`/recalls/batches/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Recall Distributions
export async function getRecallDistributions(params?: PaginationParams & { recallEventId?: string; notificationStatus?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.recallEventId) searchParams.set('recallEventId', params.recallEventId);
  if (params?.notificationStatus) searchParams.set('notificationStatus', params.notificationStatus);
  return request<PaginatedResponse<any>>(`/recalls/distributions?${searchParams}`);
}

export async function createRecallDistribution(data: {
  recallEventId: string;
  recalledBatchId?: string;
  batchNumber: string;
  distributionType: string;
  recipientType?: string;
  recipientId?: string;
  recipientName: string;
  recipientEmail?: string;
  recipientPhone?: string;
  shipDate?: string;
  orderNumber?: string;
  quantityShipped?: string;
  quantityUnit?: string;
}) {
  return request<any>('/recalls/distributions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRecallDistribution(id: string, data: Partial<{
  notificationStatus: string;
  notificationMethod: string;
  responseAction: string;
  responseNotes: string;
  quantityReturned: string;
  quantityDestroyedOnSite: string;
  followUpRequired: boolean;
  followUpDate: string;
}>) {
  return request<any>(`/recalls/distributions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Recall Notifications
export async function getRecallNotifications(params?: PaginationParams & { recallEventId?: string; deliveryStatus?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.recallEventId) searchParams.set('recallEventId', params.recallEventId);
  if (params?.deliveryStatus) searchParams.set('deliveryStatus', params.deliveryStatus);
  return request<PaginatedResponse<any>>(`/recalls/notifications?${searchParams}`);
}

export async function createRecallNotification(data: {
  recallEventId: string;
  recallDistributionId?: string;
  notificationType: string;
  recipientType: string;
  recipientName: string;
  recipientEmail?: string;
  recipientPhone?: string;
  channel: string;
  subject?: string;
  message: string;
  templateUsed?: string;
  sendNow?: boolean;
}) {
  return request<any>('/recalls/notifications', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRecallNotification(id: string, data: Partial<{
  deliveryStatus: string;
  responseReceived: boolean;
  responseSummary: string;
  acknowledged: boolean;
}>) {
  return request<any>(`/recalls/notifications/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Recall Actions
export async function getRecallActions(params?: PaginationParams & { recallEventId?: string; status?: string; assignedTo?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.recallEventId) searchParams.set('recallEventId', params.recallEventId);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.assignedTo) searchParams.set('assignedTo', params.assignedTo);
  return request<PaginatedResponse<any>>(`/recalls/actions?${searchParams}`);
}

export async function createRecallAction(data: {
  recallEventId: string;
  recalledBatchId?: string;
  actionType: string;
  actionDescription: string;
  priority?: string;
  assignedTo?: string;
  dueDate?: string;
}) {
  return request<any>('/recalls/actions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRecallAction(id: string, data: Partial<{
  status: string;
  completionNotes: string;
  verified: boolean;
  blockers: string;
}>) {
  return request<any>(`/recalls/actions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Recall Trace Links
export async function getRecallTraceLinks(params?: { recallEventId?: string; recalledBatchId?: string; traceDirection?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.recallEventId) searchParams.set('recallEventId', params.recallEventId);
  if (params?.recalledBatchId) searchParams.set('recalledBatchId', params.recalledBatchId);
  if (params?.traceDirection) searchParams.set('traceDirection', params.traceDirection);
  return request<{ data: any[] }>(`/recalls/trace-links?${searchParams}`);
}

export async function createRecallTraceLink(data: {
  recallEventId: string;
  recalledBatchId?: string;
  traceDirection: string;
  sourceBatchNumber: string;
  sourceEntityType: string;
  sourceEntityId?: string;
  targetBatchNumber: string;
  targetEntityType: string;
  targetEntityId?: string;
  relationshipType: string;
  quantityUsed?: string;
  isAffected?: boolean;
}) {
  return request<any>('/recalls/trace-links', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Mock Recall Drills
export async function getMockRecallDrills(params?: PaginationParams & { status?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  return request<PaginatedResponse<any>>(`/recalls/mock-drills?${searchParams}`);
}

export async function getMockRecallDrill(id: string) {
  return request<any>(`/recalls/mock-drills/${id}`);
}

export async function createMockRecallDrill(data: {
  drillName: string;
  drillType?: string;
  scenarioDescription: string;
  targetProductType?: string;
  targetProductId?: string;
  targetProductName?: string;
  targetBatchNumber?: string;
  drillDate: string;
  targetCompletionTime?: number;
  drillCoordinatorId?: string;
  participants?: any[];
}) {
  return request<any>('/recalls/mock-drills', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateMockRecallDrill(id: string, data: Partial<{
  drillStatus: string;
  batchesIdentified: number;
  customersIdentified: number;
  customersNotified: number;
  traceAccuracyPercent: string;
  overallRating: string;
  strengths: string[];
  weaknesses: string[];
  improvementActions: string[];
  lessonsLearned: string;
  drillReport: string;
  nextDrillDate: string;
}>) {
  return request<any>(`/recalls/mock-drills/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Recall Effectiveness Checks
export async function getRecallEffectivenessChecks(params?: { recallEventId?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.recallEventId) searchParams.set('recallEventId', params.recallEventId);
  return request<{ data: any[] }>(`/recalls/effectiveness-checks?${searchParams}`);
}

export async function createRecallEffectivenessCheck(data: {
  recallEventId: string;
  checkDate: string;
  checkType: string;
  sampleSize?: number;
  consigneesChecked?: number;
  consigneesResponded?: number;
  quantityAccounted?: string;
  quantityReturned?: string;
  quantityDestroyed?: string;
  checkMethodology?: string;
  findings?: string;
  conclusion?: string;
}) {
  return request<any>('/recalls/effectiveness-checks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRecallEffectivenessCheck(id: string, data: Partial<{
  effectivenessLevel: string;
  furtherActionRequired: boolean;
  recommendedActions: string;
  reviewed: boolean;
}>) {
  return request<any>(`/recalls/effectiveness-checks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Recall Templates
export async function getRecallTemplates(params?: { templateType?: string; isActive?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.templateType) searchParams.set('templateType', params.templateType);
  if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
  return request<{ data: any[] }>(`/recalls/templates?${searchParams}`);
}

export async function createRecallTemplate(data: {
  templateName: string;
  templateType: string;
  templateCategory?: string;
  subjectTemplate?: string;
  bodyTemplate: string;
  placeholders?: any[];
  channel?: string;
  defaultRecipients?: any[];
  language?: string;
  isDefault?: boolean;
}) {
  return request<any>('/recalls/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRecallTemplate(id: string, data: Partial<{
  templateName: string;
  subjectTemplate: string;
  bodyTemplate: string;
  placeholders: any[];
  isActive: boolean;
  isDefault: boolean;
}>) {
  return request<any>(`/recalls/templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteRecallTemplate(id: string) {
  return request<any>(`/recalls/templates/${id}`, { method: 'DELETE' });
}

// Recall Contacts
export async function getRecallContacts(params?: { contactType?: string; isActive?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.contactType) searchParams.set('contactType', params.contactType);
  if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
  return request<{ data: any[] }>(`/recalls/contacts?${searchParams}`);
}

export async function createRecallContact(data: {
  contactType: string;
  contactName: string;
  contactTitle?: string;
  organizationName?: string;
  department?: string;
  email?: string;
  phone?: string;
  mobile?: string;
  fax?: string;
  address?: string;
  isPrimary?: boolean;
  is24Hour?: boolean;
  notificationPriority?: number;
}) {
  return request<any>('/recalls/contacts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRecallContact(id: string, data: Partial<{
  contactName: string;
  contactTitle: string;
  email: string;
  phone: string;
  mobile: string;
  isPrimary: boolean;
  is24Hour: boolean;
  notificationPriority: number;
  isActive: boolean;
}>) {
  return request<any>(`/recalls/contacts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteRecallContact(id: string) {
  return request<any>(`/recalls/contacts/${id}`, { method: 'DELETE' });
}

// Recall Analytics
export async function getRecallAnalytics(params?: { periodType?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.periodType) searchParams.set('periodType', params.periodType);
  return request<{ data: any[] }>(`/recalls/analytics?${searchParams}`);
}

// Recall Dashboard
export async function getRecallDashboard() {
  return request<{
    activeRecalls: number;
    totalRecallsThisYear: number;
    pendingActions: number;
    pendingNotifications: number;
    recentRecalls: any[];
    upcomingDrills: any[];
  }>('/recalls/dashboard');
}

// ============================================================================
// PHASE 50: COMPLAINT MANAGEMENT & CAPA
// ============================================================================

// Complaint Categories
export async function getComplaintCategories() {
  return request<{ data: any[] }>('/complaints-capa/categories');
}

export async function getComplaintCategory(id: string) {
  return request<any>(`/complaints-capa/categories/${id}`);
}

export async function createComplaintCategory(data: {
  categoryCode: string;
  categoryName: string;
  categoryType: string;
  parentCategoryId?: string;
  description?: string;
  severityDefault?: string;
  requiresCapa?: boolean;
  requiresInvestigation?: boolean;
  responseTimeHours?: number;
  escalationTimeHours?: number;
  regulatoryReportable?: boolean;
}) {
  return request<any>('/complaints-capa/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateComplaintCategory(id: string, data: Partial<{
  categoryName: string;
  description: string;
  severityDefault: string;
  requiresCapa: boolean;
  responseTimeHours: number;
  escalationTimeHours: number;
  isActive: boolean;
}>) {
  return request<any>(`/complaints-capa/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Customer Complaints
export async function getCustomerComplaints(params?: PaginationParams & {
  status?: string;
  priority?: string;
  categoryId?: string;
  customerId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.priority) searchParams.set('priority', params.priority);
  if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
  if (params?.customerId) searchParams.set('customerId', params.customerId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/complaints-capa/complaints?${searchParams}`);
}

export async function getCustomerComplaint(id: string) {
  return request<any>(`/complaints-capa/complaints/${id}`);
}

export async function createCustomerComplaint(data: {
  complaintTitle: string;
  complaintType: string;
  categoryId?: string;
  sourceType: string;
  customerName: string;
  customerId?: string;
  customerContact?: string;
  customerEmail?: string;
  customerPhone?: string;
  receivedDate: string;
  receivedChannel?: string;
  productType?: string;
  productId?: string;
  productName?: string;
  batchNumber?: string;
  complaintDescription: string;
  customerExpectation?: string;
  priority?: string;
  severity?: string;
}) {
  return request<any>('/complaints-capa/complaints', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCustomerComplaint(id: string, data: Partial<{
  complaintStatus: string;
  priority: string;
  severity: string;
  assignedTo: string;
  investigationStatus: string;
  rootCause: string;
  rootCauseCategory: string;
  resolutionDescription: string;
  resolutionType: string;
  customerSatisfaction: string;
  capaRequired: boolean;
  capaId: string;
  escalated: boolean;
  escalatedTo: string;
  escalationReason: string;
}>) {
  return request<any>(`/complaints-capa/complaints/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCustomerComplaint(id: string) {
  return request<any>(`/complaints-capa/complaints/${id}`, { method: 'DELETE' });
}

// Complaint Investigations
export async function getComplaintInvestigations(params?: PaginationParams & {
  status?: string;
  complaintId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.complaintId) searchParams.set('complaintId', params.complaintId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/complaints-capa/investigations?${searchParams}`);
}

export async function getComplaintInvestigation(id: string) {
  return request<any>(`/complaints-capa/investigations/${id}`);
}

export async function createComplaintInvestigation(data: {
  complaintId: string;
  investigationType: string;
  scopeDescription?: string;
  investigationObjectives?: string[];
  investigationMethods?: string[];
  leadInvestigatorId?: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
}) {
  return request<any>('/complaints-capa/investigations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateComplaintInvestigation(id: string, data: Partial<{
  investigationStatus: string;
  actualStartDate: string;
  actualEndDate: string;
  observations: string;
  analysisSummary: string;
  rootCauseIdentified: boolean;
  rootCauseDescription: string;
  rootCauseMethod: string;
  conclusion: string;
  capaRecommended: boolean;
  approvalStatus: string;
}>) {
  return request<any>(`/complaints-capa/investigations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Complaint Samples
export async function getComplaintSamples(params?: PaginationParams & {
  status?: string;
  complaintId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.complaintId) searchParams.set('complaintId', params.complaintId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/complaints-capa/samples?${searchParams}`);
}

export async function getComplaintSample(id: string) {
  return request<any>(`/complaints-capa/samples/${id}`);
}

export async function createComplaintSample(data: {
  complaintId: string;
  investigationId?: string;
  sampleType: string;
  productType?: string;
  productId?: string;
  productName?: string;
  batchNumber?: string;
  quantity?: number;
  quantityUnit?: string;
}) {
  return request<any>('/complaints-capa/samples', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateComplaintSample(id: string, data: Partial<{
  sampleStatus: string;
  receivedDate: string;
  receivedCondition: string;
  storageLocation: string;
  testResultsSummary: string;
  passFail: string;
  disposition: string;
  dispositionNotes: string;
}>) {
  return request<any>(`/complaints-capa/samples/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// CAPA Records
export async function getCapaRecords(params?: PaginationParams & {
  status?: string;
  capaType?: string;
  priority?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.capaType) searchParams.set('capaType', params.capaType);
  if (params?.priority) searchParams.set('priority', params.priority);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/complaints-capa/capas?${searchParams}`);
}

export async function getCapaRecord(id: string) {
  return request<any>(`/complaints-capa/capas/${id}`);
}

export async function createCapaRecord(data: {
  capaTitle: string;
  capaType: string;
  sourceType: string;
  sourceId?: string;
  sourceReference?: string;
  complaintId?: string;
  problemDescription: string;
  problemScope?: string;
  impactDescription?: string;
  priority?: string;
  riskLevel?: string;
  initiationDate: string;
  targetCompletionDate?: string;
  capaOwnerId?: string;
  department?: string;
  containmentActions?: string;
}) {
  return request<any>('/complaints-capa/capas', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCapaRecord(id: string, data: Partial<{
  capaStatus: string;
  priority: string;
  rootCauseSummary: string;
  rootCauseCategory: string;
  targetCompletionDate: string;
  actualCompletionDate: string;
  capaOwnerId: string;
  approvalStatus: string;
  closureSummary: string;
  closureVerified: boolean;
  effectivenessVerified: boolean;
  effectivenessResult: string;
}>) {
  return request<any>(`/complaints-capa/capas/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCapaRecord(id: string) {
  return request<any>(`/complaints-capa/capas/${id}`, { method: 'DELETE' });
}

// CAPA Actions
export async function getCapaActions(params?: PaginationParams & {
  status?: string;
  capaId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.capaId) searchParams.set('capaId', params.capaId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/complaints-capa/capa-actions?${searchParams}`);
}

export async function getCapaAction(id: string) {
  return request<any>(`/complaints-capa/capa-actions/${id}`);
}

export async function createCapaAction(data: {
  capaId: string;
  actionType: string;
  actionDescription: string;
  priority?: string;
  assignedTo?: string;
  assignedDepartment?: string;
  dueDate?: string;
  verificationRequired?: boolean;
  verificationMethod?: string;
}) {
  return request<any>('/complaints-capa/capa-actions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCapaAction(id: string, data: Partial<{
  actionStatus: string;
  startedDate: string;
  completedDate: string;
  completionNotes: string;
  verifiedBy: string;
  verifiedDate: string;
  verificationResult: string;
  verificationNotes: string;
}>) {
  return request<any>(`/complaints-capa/capa-actions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Root Cause Analyses
export async function getRootCauseAnalyses(params?: PaginationParams & {
  status?: string;
  analysisType?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.analysisType) searchParams.set('analysisType', params.analysisType);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/complaints-capa/root-cause-analyses?${searchParams}`);
}

export async function getRootCauseAnalysis(id: string) {
  return request<any>(`/complaints-capa/root-cause-analyses/${id}`);
}

export async function createRootCauseAnalysis(data: {
  analysisTitle: string;
  analysisType: string;
  sourceType: string;
  sourceId?: string;
  sourceReference?: string;
  capaId?: string;
  complaintId?: string;
  problemStatement: string;
  problemDate?: string;
  problemLocation?: string;
  facilitatorId?: string;
}) {
  return request<any>('/complaints-capa/root-cause-analyses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRootCauseAnalysis(id: string, data: Partial<{
  analysisStatus: string;
  why1: string;
  why1Evidence: string;
  why2: string;
  why2Evidence: string;
  why3: string;
  why3Evidence: string;
  why4: string;
  why4Evidence: string;
  why5: string;
  why5Evidence: string;
  primaryRootCause: string;
  approvalStatus: string;
}>) {
  return request<any>(`/complaints-capa/root-cause-analyses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// CAPA Effectiveness Reviews
export async function getCapaEffectivenessReviews(params?: PaginationParams & {
  status?: string;
  capaId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.capaId) searchParams.set('capaId', params.capaId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/complaints-capa/effectiveness-reviews?${searchParams}`);
}

export async function getCapaEffectivenessReview(id: string) {
  return request<any>(`/complaints-capa/effectiveness-reviews/${id}`);
}

export async function createCapaEffectivenessReview(data: {
  capaId: string;
  reviewType: string;
  scheduledDate: string;
  reviewPeriodStart?: string;
  reviewPeriodEnd?: string;
  reviewerId?: string;
  successCriteria?: any[];
  metricsToEvaluate?: any[];
  evaluationMethod?: string;
}) {
  return request<any>('/complaints-capa/effectiveness-reviews', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCapaEffectivenessReview(id: string, data: Partial<{
  reviewStatus: string;
  actualDate: string;
  effectivenessRating: string;
  recurrenceObserved: boolean;
  recurrenceDetails: string;
  improvementObserved: boolean;
  findings: string;
  recommendation: string;
  nextReviewDate: string;
}>) {
  return request<any>(`/complaints-capa/effectiveness-reviews/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Complaint Response Templates
export async function getComplaintResponseTemplates(params?: { templateType?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.templateType) searchParams.set('templateType', params.templateType);
  return request<{ data: any[] }>(`/complaints-capa/response-templates?${searchParams}`);
}

export async function getComplaintResponseTemplate(id: string) {
  return request<any>(`/complaints-capa/response-templates/${id}`);
}

export async function createComplaintResponseTemplate(data: {
  templateName: string;
  templateType: string;
  categoryId?: string;
  subjectTemplate?: string;
  bodyTemplate: string;
  language?: string;
  isDefault?: boolean;
}) {
  return request<any>('/complaints-capa/response-templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateComplaintResponseTemplate(id: string, data: Partial<{
  templateName: string;
  subjectTemplate: string;
  bodyTemplate: string;
  isDefault: boolean;
  isActive: boolean;
}>) {
  return request<any>(`/complaints-capa/response-templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Complaint Communications
export async function getComplaintCommunications(params?: PaginationParams & {
  complaintId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.complaintId) searchParams.set('complaintId', params.complaintId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/complaints-capa/communications?${searchParams}`);
}

export async function createComplaintCommunication(data: {
  complaintId: string;
  communicationType: string;
  direction: string;
  channel: string;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  subject?: string;
  message: string;
  templateUsed?: string;
}) {
  return request<any>('/complaints-capa/communications', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Complaint Trends
export async function getComplaintTrends(params?: { period?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.period) searchParams.set('period', params.period);
  return request<{ data: any[] }>(`/complaints-capa/trends?${searchParams}`);
}

// CAPA Analytics
export async function getCapaAnalytics(params?: { period?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.period) searchParams.set('period', params.period);
  return request<{ data: any[] }>(`/complaints-capa/capa-analytics?${searchParams}`);
}

// Complaints & CAPA Dashboard
export async function getComplaintsCapaDashboard() {
  return request<{
    summary: {
      openComplaints: number;
      newComplaints30Days: number;
      escalatedComplaints: number;
      openCapas: number;
      overdueCapas: number;
      pendingActions: number;
    };
    recentComplaints: any[];
    recentCapas: any[];
  }>('/complaints-capa/dashboard');
}

// ============================================================================
// PHASE 51: CHANGE CONTROL MANAGEMENT
// ============================================================================

// Change Types
export async function getChangeTypes() {
  return request<{ data: any[] }>('/change-control/types');
}

export async function getChangeType(id: string) {
  return request<any>(`/change-control/types/${id}`);
}

export async function createChangeType(data: {
  typeCode: string;
  typeName: string;
  category: string;
  description?: string;
  requiresImpactAssessment?: boolean;
  requiresValidation?: boolean;
  requiresRegulatoryReview?: boolean;
  requiresCustomerNotification?: boolean;
  defaultPriority?: string;
  approvalLevels?: number;
}) {
  return request<any>('/change-control/types', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateChangeType(id: string, data: Partial<{
  typeName: string;
  description: string;
  requiresImpactAssessment: boolean;
  requiresValidation: boolean;
  approvalLevels: number;
  isActive: boolean;
}>) {
  return request<any>(`/change-control/types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Change Requests
export async function getChangeRequests(params?: PaginationParams & {
  status?: string;
  category?: string;
  priority?: string;
  ownerId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.category) searchParams.set('category', params.category);
  if (params?.priority) searchParams.set('priority', params.priority);
  if (params?.ownerId) searchParams.set('ownerId', params.ownerId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/change-control/requests?${searchParams}`);
}

export async function getChangeRequest(id: string) {
  return request<any>(`/change-control/requests/${id}`);
}

export async function createChangeRequest(data: {
  changeTitle: string;
  changeTypeId?: string;
  changeCategory: string;
  changeDescription: string;
  reasonForChange: string;
  expectedBenefits?: string;
  currentState?: string;
  proposedState?: string;
  priority?: string;
  urgency?: string;
  department?: string;
  targetImplementationDate?: string;
  targetCompletionDate?: string;
  estimatedCost?: number;
}) {
  return request<any>('/change-control/requests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateChangeRequest(id: string, data: Partial<{
  changeStatus: string;
  priority: string;
  urgency: string;
  changeOwnerId: string;
  changeCoordinatorId: string;
  riskLevel: string;
  impactAssessmentStatus: string;
  validationStatus: string;
  targetImplementationDate: string;
  actualImplementationDate: string;
  targetCompletionDate: string;
  actualCompletionDate: string;
  closureSummary: string;
  effectivenessVerified: boolean;
}>) {
  return request<any>(`/change-control/requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteChangeRequest(id: string) {
  return request<any>(`/change-control/requests/${id}`, { method: 'DELETE' });
}

// Change Impact Assessments
export async function getChangeImpactAssessments(params?: PaginationParams & {
  status?: string;
  changeRequestId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.changeRequestId) searchParams.set('changeRequestId', params.changeRequestId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/change-control/impact-assessments?${searchParams}`);
}

export async function getChangeImpactAssessment(id: string) {
  return request<any>(`/change-control/impact-assessments/${id}`);
}

export async function createChangeImpactAssessment(data: {
  changeRequestId: string;
  assessmentType: string;
  qualityImpact?: string;
  safetyImpact?: string;
  regulatoryImpact?: string;
  customerImpact?: string;
  productionImpact?: string;
  supplyChainImpact?: string;
  costImpact?: string;
  timelineImpact?: string;
  overallRiskLevel?: string;
}) {
  return request<any>('/change-control/impact-assessments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateChangeImpactAssessment(id: string, data: Partial<{
  assessmentStatus: string;
  qualityImpact: string;
  qualityImpactDetails: string;
  safetyImpact: string;
  regulatoryImpact: string;
  customerImpact: string;
  productionImpact: string;
  overallRiskLevel: string;
  recommendation: string;
  conditions: string;
}>) {
  return request<any>(`/change-control/impact-assessments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Change Approvals
export async function getChangeApprovals(params?: PaginationParams & {
  status?: string;
  changeRequestId?: string;
  approverId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.changeRequestId) searchParams.set('changeRequestId', params.changeRequestId);
  if (params?.approverId) searchParams.set('approverId', params.approverId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/change-control/approvals?${searchParams}`);
}

export async function getChangeApproval(id: string) {
  return request<any>(`/change-control/approvals/${id}`);
}

export async function createChangeApproval(data: {
  changeRequestId: string;
  approvalLevel: number;
  approvalType: string;
  approverRole?: string;
  approverId?: string;
  requestedDate: string;
  dueDate?: string;
}) {
  return request<any>('/change-control/approvals', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateChangeApproval(id: string, data: Partial<{
  approvalStatus: string;
  decision: string;
  conditions: string;
  comments: string;
  delegatedTo: string;
  delegationReason: string;
}>) {
  return request<any>(`/change-control/approvals/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Change Implementation Tasks
export async function getChangeImplementationTasks(params?: PaginationParams & {
  status?: string;
  changeRequestId?: string;
  assignedTo?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.changeRequestId) searchParams.set('changeRequestId', params.changeRequestId);
  if (params?.assignedTo) searchParams.set('assignedTo', params.assignedTo);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/change-control/tasks?${searchParams}`);
}

export async function getChangeImplementationTask(id: string) {
  return request<any>(`/change-control/tasks/${id}`);
}

export async function createChangeImplementationTask(data: {
  changeRequestId: string;
  taskName: string;
  taskDescription?: string;
  taskType: string;
  priority?: string;
  assignedTo?: string;
  assignedDepartment?: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
  verificationRequired?: boolean;
}) {
  return request<any>('/change-control/tasks', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateChangeImplementationTask(id: string, data: Partial<{
  taskStatus: string;
  completionPercentage: number;
  completionNotes: string;
  actualStartDate: string;
  actualEndDate: string;
  verifiedBy: string;
  verificationResult: string;
  blockers: string;
}>) {
  return request<any>(`/change-control/tasks/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Change Training Records
export async function getChangeTrainingRecords(params?: PaginationParams & {
  status?: string;
  changeRequestId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.changeRequestId) searchParams.set('changeRequestId', params.changeRequestId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/change-control/training?${searchParams}`);
}

export async function createChangeTrainingRecord(data: {
  changeRequestId: string;
  implementationTaskId?: string;
  trainingType: string;
  trainingTitle: string;
  trainingDescription?: string;
  scheduledDate?: string;
  trainerId?: string;
  assessmentRequired?: boolean;
}) {
  return request<any>('/change-control/training', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateChangeTrainingRecord(id: string, data: Partial<{
  trainingStatus: string;
  actualDate: string;
  attendanceCount: number;
  passRate: number;
}>) {
  return request<any>(`/change-control/training/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Change Effectiveness Reviews
export async function getChangeEffectivenessReviews(params?: PaginationParams & {
  status?: string;
  changeRequestId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.changeRequestId) searchParams.set('changeRequestId', params.changeRequestId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/change-control/effectiveness-reviews?${searchParams}`);
}

export async function createChangeEffectivenessReview(data: {
  changeRequestId: string;
  reviewType: string;
  scheduledDate: string;
  reviewPeriodStart?: string;
  reviewPeriodEnd?: string;
  reviewerId?: string;
}) {
  return request<any>('/change-control/effectiveness-reviews', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateChangeEffectivenessReview(id: string, data: Partial<{
  reviewStatus: string;
  actualDate: string;
  objectivesAchieved: boolean;
  benefitsRealized: boolean;
  effectivenessRating: string;
  recommendation: string;
  nextReviewDate: string;
}>) {
  return request<any>(`/change-control/effectiveness-reviews/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Change History
export async function getChangeHistory(changeRequestId: string) {
  return request<{ data: any[] }>(`/change-control/history?changeRequestId=${changeRequestId}`);
}

// Change Templates
export async function getChangeTemplates(params?: { templateType?: string; changeCategory?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.templateType) searchParams.set('templateType', params.templateType);
  if (params?.changeCategory) searchParams.set('changeCategory', params.changeCategory);
  return request<{ data: any[] }>(`/change-control/templates?${searchParams}`);
}

export async function createChangeTemplate(data: {
  templateName: string;
  templateType: string;
  changeCategory?: string;
  description?: string;
  defaultTitleTemplate?: string;
  defaultDescriptionTemplate?: string;
  defaultChecklist?: any[];
  isDefault?: boolean;
}) {
  return request<any>('/change-control/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateChangeTemplate(id: string, data: Partial<{
  templateName: string;
  description: string;
  defaultChecklist: any[];
  isDefault: boolean;
  isActive: boolean;
}>) {
  return request<any>(`/change-control/templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Change Analytics
export async function getChangeAnalytics(params?: { period?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.period) searchParams.set('period', params.period);
  return request<{ data: any[] }>(`/change-control/analytics?${searchParams}`);
}

// Change Control Dashboard
export async function getChangeControlDashboard() {
  return request<{
    summary: {
      openChanges: number;
      pendingApproval: number;
      inImplementation: number;
      newChanges30Days: number;
      pendingTasks: number;
      overdueChanges: number;
    };
    recentChanges: any[];
    pendingApprovals: any[];
  }>('/change-control/dashboard');
}

// ============================================================================
// PHASE 52: DEVIATION MANAGEMENT
// ============================================================================

// Deviation Types
export async function getDeviationTypes() {
  return request<{ data: any[] }>('/deviations/types');
}

export async function getDeviationType(id: string) {
  return request<any>(`/deviations/types/${id}`);
}

export async function createDeviationType(data: {
  typeCode: string;
  typeName: string;
  category: string;
  description?: string;
  severityDefault?: string;
  requiresInvestigation?: boolean;
  requiresCapa?: boolean;
  maxClosureDays?: number;
  notificationRoles?: string[];
  escalationRules?: any[];
}) {
  return request<any>('/deviations/types', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDeviationType(id: string, data: Partial<{
  typeName: string;
  description: string;
  severityDefault: string;
  requiresInvestigation: boolean;
  requiresCapa: boolean;
  maxClosureDays: number;
  isActive: boolean;
}>) {
  return request<any>(`/deviations/types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Deviations
export async function getDeviations(params?: PaginationParams & {
  status?: string;
  severity?: string;
  category?: string;
  assignedTo?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.severity) searchParams.set('severity', params.severity);
  if (params?.category) searchParams.set('category', params.category);
  if (params?.assignedTo) searchParams.set('assignedTo', params.assignedTo);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/deviations?${searchParams}`);
}

export async function getDeviation(id: string) {
  return request<any>(`/deviations/${id}`);
}

export async function createDeviation(data: {
  deviationTitle: string;
  deviationTypeId?: string;
  deviationCategory: string;
  severity?: string;
  classification?: string;
  discoveredDate: string;
  discoveredBy: string;
  discoveredDuring?: string;
  discoveryLocation?: string;
  deviationDescription: string;
  expectedOutcome?: string;
  actualOutcome?: string;
  immediateAction?: string;
  immediateActionBy?: string;
  immediateActionDate?: string;
  affectedBatchNumbers?: string[];
  affectedProducts?: string[];
  affectedMaterials?: string[];
  affectedEquipment?: string[];
  affectedAreas?: string[];
  quantityAffected?: string;
  quantityUnit?: string;
  sopReference?: string;
  sopSection?: string;
  deviationFromSop?: string;
  investigationRequired?: boolean;
  dueDate?: string;
  assignedTo?: string;
  assignedDepartment?: string;
}) {
  return request<any>('/deviations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDeviation(id: string, data: Partial<{
  deviationStatus: string;
  severity: string;
  investigationStatus: string;
  investigationStartDate: string;
  investigationEndDate: string;
  rootCause: string;
  rootCauseCategory: string;
  rootCauseMethod: string;
  productImpact: string;
  qualityImpact: string;
  safetyImpact: string;
  regulatoryImpact: string;
  batchDisposition: string;
  capaRequired: boolean;
  capaId: string;
  riskLevel: string;
  riskScore: number;
  riskJustification: string;
  isRecurring: boolean;
  dueDate: string;
  assignedTo: string;
  reviewerId: string;
  reviewDate: string;
  reviewComments: string;
  approverId: string;
  approvalDate: string;
  approvalComments: string;
  closureSummary: string;
  effectivenessVerified: boolean;
  effectivenessDate: string;
  effectivenessNotes: string;
}>) {
  return request<any>(`/deviations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteDeviation(id: string) {
  return request<any>(`/deviations/${id}`, { method: 'DELETE' });
}

// Deviation Investigations
export async function getDeviationInvestigations(params?: PaginationParams & {
  status?: string;
  deviationId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.deviationId) searchParams.set('deviationId', params.deviationId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/deviations/investigations?${searchParams}`);
}

export async function getDeviationInvestigation(id: string) {
  return request<any>(`/deviations/investigations/${id}`);
}

export async function createDeviationInvestigation(data: {
  deviationId: string;
  investigationType: string;
  leadInvestigatorId?: string;
  investigationTeam?: any[];
  startDate?: string;
  targetEndDate?: string;
  investigationScope?: string;
  investigationObjectives?: string[];
  investigationMethods?: string[];
}) {
  return request<any>('/deviations/investigations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDeviationInvestigation(id: string, data: Partial<{
  investigationStatus: string;
  actualEndDate: string;
  observations: string;
  timelineOfEvents: string;
  contributingFactors: any[];
  immediateCauses: any[];
  rootCauses: any[];
  systemicIssues: any[];
  rcaMethod: string;
  why1: string;
  why2: string;
  why3: string;
  why4: string;
  why5: string;
  conclusion: string;
  findingsSummary: string;
  recommendations: any[];
  preventiveMeasures: any[];
  approvalStatus: string;
  approvedBy: string;
}>) {
  return request<any>(`/deviations/investigations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Deviation Impact Assessments
export async function getDeviationImpactAssessments(params?: PaginationParams & {
  status?: string;
  deviationId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.deviationId) searchParams.set('deviationId', params.deviationId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/deviations/impact-assessments?${searchParams}`);
}

export async function getDeviationImpactAssessment(id: string) {
  return request<any>(`/deviations/impact-assessments/${id}`);
}

export async function createDeviationImpactAssessment(data: {
  deviationId: string;
  assessmentType: string;
  assessorId?: string;
  assessmentDate?: string;
  productQualityImpact?: string;
  productQualityDetails?: string;
  safetyImpact?: string;
  safetyDetails?: string;
  regulatoryImpact?: string;
  regulatoryDetails?: string;
  regulatoryNotificationRequired?: boolean;
  batchImpact?: string;
  batchesAffected?: any[];
  totalQuantityAffected?: string;
  recommendedDisposition?: string;
  customerImpact?: string;
  customerDetails?: string;
  estimatedCost?: number;
}) {
  return request<any>('/deviations/impact-assessments', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDeviationImpactAssessment(id: string, data: Partial<{
  assessmentStatus: string;
  productQualityImpact: string;
  safetyImpact: string;
  regulatoryImpact: string;
  customerImpact: string;
  overallRiskLevel: string;
  riskScore: number;
  capaRecommended: boolean;
  additionalActions: any[];
  reviewedBy: string;
  reviewedDate: string;
  reviewComments: string;
}>) {
  return request<any>(`/deviations/impact-assessments/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Deviation Actions
export async function getDeviationActions(params?: PaginationParams & {
  status?: string;
  deviationId?: string;
  actionType?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.deviationId) searchParams.set('deviationId', params.deviationId);
  if (params?.actionType) searchParams.set('actionType', params.actionType);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/deviations/actions?${searchParams}`);
}

export async function getDeviationAction(id: string) {
  return request<any>(`/deviations/actions/${id}`);
}

export async function createDeviationAction(data: {
  deviationId: string;
  actionType: string;
  actionDescription: string;
  priority?: string;
  assignedTo?: string;
  assignedDepartment?: string;
  dueDate?: string;
  verificationRequired?: boolean;
  verificationMethod?: string;
}) {
  return request<any>('/deviations/actions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDeviationAction(id: string, data: Partial<{
  actionStatus: string;
  startedDate: string;
  completedDate: string;
  completionNotes: string;
  completedBy: string;
  verifiedBy: string;
  verifiedDate: string;
  verificationResult: string;
  verificationNotes: string;
}>) {
  return request<any>(`/deviations/actions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Deviation Extensions
export async function getDeviationExtensions(params?: PaginationParams & {
  deviationId?: string;
  approvalStatus?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.deviationId) searchParams.set('deviationId', params.deviationId);
  if (params?.approvalStatus) searchParams.set('approvalStatus', params.approvalStatus);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/deviations/extensions?${searchParams}`);
}

export async function createDeviationExtension(data: {
  deviationId: string;
  extensionType: string;
  relatedActionId?: string;
  currentDueDate: string;
  requestedDueDate: string;
  reason: string;
  justification?: string;
}) {
  return request<any>('/deviations/extensions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDeviationExtension(id: string, data: Partial<{
  approvalStatus: string;
  approvedBy: string;
  approvalComments: string;
}>) {
  return request<any>(`/deviations/extensions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Deviation Templates
export async function getDeviationTemplates(params?: { templateType?: string; deviationCategory?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.templateType) searchParams.set('templateType', params.templateType);
  if (params?.deviationCategory) searchParams.set('deviationCategory', params.deviationCategory);
  return request<{ data: any[] }>(`/deviations/templates?${searchParams}`);
}

export async function createDeviationTemplate(data: {
  templateName: string;
  templateType: string;
  deviationCategory?: string;
  description?: string;
  defaultTitleTemplate?: string;
  defaultDescriptionTemplate?: string;
  defaultInvestigationScope?: string;
  defaultActions?: any[];
  requiredFields?: string[];
  isDefault?: boolean;
}) {
  return request<any>('/deviations/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDeviationTemplate(id: string, data: Partial<{
  templateName: string;
  description: string;
  defaultActions: any[];
  requiredFields: string[];
  isDefault: boolean;
  isActive: boolean;
}>) {
  return request<any>(`/deviations/templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Deviation Trends
export async function getDeviationTrends(params?: { period?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.period) searchParams.set('period', params.period);
  return request<{ data: any[] }>(`/deviations/trends?${searchParams}`);
}

// Deviation Dashboard
export async function getDeviationDashboard() {
  return request<{
    summary: {
      openDeviations: number;
      newDeviations30Days: number;
      pendingInvestigation: number;
      overdueDeviations: number;
      pendingActions: number;
      capaLinked: number;
    };
    recentDeviations: any[];
    pendingApprovals: any[];
  }>('/deviations/dashboard');
}

// ============================================================================
// PHASE 53: AUDIT MANAGEMENT
// ============================================================================

// Audit Programs
export async function getAuditPrograms(params?: { status?: string; year?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.year) searchParams.set('year', params.year.toString());
  return request<{ data: any[] }>(`/audits/programs?${searchParams}`);
}

export async function getAuditProgram(id: string) {
  return request<any>(`/audits/programs/${id}`);
}

export async function createAuditProgram(data: {
  programName: string;
  programType: string;
  programCode?: string;
  description?: string;
  objectives?: string[];
  scope?: string;
  year?: number;
  status?: string;
  startDate?: string;
  endDate?: string;
  riskBased?: boolean;
  frequency?: string;
  totalAuditsPlanned?: number;
  programOwnerId?: string;
  budget?: number;
}) {
  return request<any>('/audits/programs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAuditProgram(id: string, data: Partial<{
  programName: string;
  programType: string;
  description: string;
  objectives: string[];
  scope: string;
  status: string;
  startDate: string;
  endDate: string;
  totalAuditsPlanned: number;
  totalAuditsCompleted: number;
  budget: number;
  actualCost: number;
  approvedBy: string;
  approvedDate: string;
}>) {
  return request<any>(`/audits/programs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Audit Types
export async function getAuditTypes(params?: { category?: string; isActive?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.category) searchParams.set('category', params.category);
  if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
  return request<{ data: any[] }>(`/audits/types?${searchParams}`);
}

export async function getAuditType(id: string) {
  return request<any>(`/audits/types/${id}`);
}

export async function createAuditType(data: {
  typeCode: string;
  typeName: string;
  auditCategory: string;
  description?: string;
  standardDurationDays?: number;
  checklistRequired?: boolean;
  defaultChecklistId?: string;
  requiresCertification?: boolean;
  certificationBody?: string;
  frequencyRecommendation?: string;
  notificationDays?: number;
}) {
  return request<any>('/audits/types', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAuditType(id: string, data: Partial<{
  typeName: string;
  description: string;
  standardDurationDays: number;
  checklistRequired: boolean;
  notificationDays: number;
  isActive: boolean;
}>) {
  return request<any>(`/audits/types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Audit Checklists
export async function getAuditChecklists(params?: { checklistType?: string; isActive?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.checklistType) searchParams.set('checklistType', params.checklistType);
  if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
  return request<{ data: any[] }>(`/audits/checklists?${searchParams}`);
}

export async function getAuditChecklist(id: string) {
  return request<any>(`/audits/checklists/${id}`);
}

export async function createAuditChecklist(data: {
  checklistName: string;
  checklistType: string;
  checklistCode?: string;
  standardReference?: string;
  version?: string;
  description?: string;
  sections?: any[];
  totalItems?: number;
  maxScore?: number;
  passingScore?: number;
  effectiveDate?: string;
  expiryDate?: string;
}) {
  return request<any>('/audits/checklists', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAuditChecklist(id: string, data: Partial<{
  checklistName: string;
  description: string;
  sections: any[];
  totalItems: number;
  maxScore: number;
  passingScore: number;
  isActive: boolean;
  approvedBy: string;
  approvedDate: string;
}>) {
  return request<any>(`/audits/checklists/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Audits
export async function getAudits(params?: PaginationParams & {
  status?: string;
  category?: string;
  programId?: string;
  leadAuditorId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.category) searchParams.set('category', params.category);
  if (params?.programId) searchParams.set('programId', params.programId);
  if (params?.leadAuditorId) searchParams.set('leadAuditorId', params.leadAuditorId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/audits?${searchParams}`);
}

export async function getAudit(id: string) {
  return request<any>(`/audits/${id}`);
}

export async function createAudit(data: {
  auditTitle: string;
  auditCategory: string;
  auditProgramId?: string;
  auditTypeId?: string;
  auditObjective?: string;
  auditScope?: string;
  auditCriteria?: string;
  plannedStartDate?: string;
  plannedEndDate?: string;
  durationDays?: number;
  auditeeType?: string;
  auditeeId?: string;
  auditeeName?: string;
  auditeeContact?: string;
  auditeeEmail?: string;
  auditLocation?: string;
  departmentsAudited?: string[];
  processesAudited?: string[];
  leadAuditorId?: string;
  leadAuditorName?: string;
  auditTeam?: any[];
  externalAuditorOrg?: string;
  checklistId?: string;
}) {
  return request<any>('/audits', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAudit(id: string, data: Partial<{
  auditTitle: string;
  auditStatus: string;
  auditObjective: string;
  auditScope: string;
  plannedStartDate: string;
  plannedEndDate: string;
  actualStartDate: string;
  actualEndDate: string;
  auditTeam: any[];
  checklistScore: number;
  checklistMaxScore: number;
  scorePercentage: number;
  auditConclusion: string;
  auditRecommendation: string;
  overallRating: string;
  strengths: string[];
  weaknesses: string[];
  reportStatus: string;
  reportDate: string;
  executiveSummary: string;
  detailedFindings: string;
  followUpRequired: boolean;
  followUpDate: string;
  capaRequired: boolean;
  closedDate: string;
  closedBy: string;
  closureNotes: string;
}>) {
  return request<any>(`/audits/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteAudit(id: string) {
  return request<any>(`/audits/${id}`, { method: 'DELETE' });
}

// Audit Findings
export async function getAuditFindings(params?: PaginationParams & {
  auditId?: string;
  status?: string;
  findingType?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.auditId) searchParams.set('auditId', params.auditId);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.findingType) searchParams.set('findingType', params.findingType);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/audits/findings?${searchParams}`);
}

export async function getAuditFinding(id: string) {
  return request<any>(`/audits/findings/${id}`);
}

export async function createAuditFinding(data: {
  auditId: string;
  findingType: string;
  findingTitle: string;
  findingDescription: string;
  clauseReference?: string;
  procedureReference?: string;
  evidence?: string;
  evidenceAttachments?: any[];
  department?: string;
  processArea?: string;
  location?: string;
  riskLevel?: string;
  riskScore?: number;
  potentialImpact?: string;
  rootCauseRequired?: boolean;
  correctiveActionRequired?: boolean;
  correctionDueDate?: string;
  correctiveActionDueDate?: string;
  assignedTo?: string;
  assignedDepartment?: string;
}) {
  return request<any>('/audits/findings', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAuditFinding(id: string, data: Partial<{
  findingStatus: string;
  rootCause: string;
  rootCauseCategory: string;
  immediateCorrection: string;
  correctionCompletedDate: string;
  correctionVerified: boolean;
  correctionVerifiedBy: string;
  correctiveAction: string;
  correctiveActionCompletedDate: string;
  correctiveActionVerified: boolean;
  correctiveActionVerifiedBy: string;
  capaId: string;
  capaNumber: string;
  acceptedByAuditee: boolean;
  auditeeResponse: string;
  closedDate: string;
  closedBy: string;
  closureNotes: string;
  effectivenessVerified: boolean;
  effectivenessDate: string;
  effectivenessNotes: string;
}>) {
  return request<any>(`/audits/findings/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Audit Checklist Responses
export async function getAuditChecklistResponses(auditId: string) {
  return request<{ data: any[] }>(`/audits/checklist-responses?auditId=${auditId}`);
}

export async function createAuditChecklistResponse(data: {
  auditId: string;
  checklistId: string;
  sectionName?: string;
  itemNumber?: number;
  question: string;
  category?: string;
  expectedEvidence?: string;
  response?: string;
  score?: number;
  maxScore?: number;
  weightedScore?: number;
  evidenceProvided?: string;
  evidenceAdequate?: boolean;
  findingId?: string;
  auditorNotes?: string;
  auditeeNotes?: string;
}) {
  return request<any>('/audits/checklist-responses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAuditChecklistResponse(id: string, data: Partial<{
  response: string;
  score: number;
  evidenceProvided: string;
  evidenceAdequate: boolean;
  findingId: string;
  auditorNotes: string;
  auditeeNotes: string;
}>) {
  return request<any>(`/audits/checklist-responses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Audit Schedules
export async function getAuditSchedules(params?: { programId?: string; isActive?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.programId) searchParams.set('programId', params.programId);
  if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
  return request<{ data: any[] }>(`/audits/schedules?${searchParams}`);
}

export async function createAuditSchedule(data: {
  scheduleName: string;
  frequency: string;
  auditProgramId?: string;
  auditTypeId?: string;
  auditeeType?: string;
  auditeeId?: string;
  auditeeName?: string;
  department?: string;
  processArea?: string;
  scheduledMonth?: number;
  scheduledQuarter?: number;
  nextDueDate?: string;
  estimatedDurationDays?: number;
  requiredAuditors?: number;
  checklistId?: string;
  isMandatory?: boolean;
  riskRating?: string;
  priority?: number;
}) {
  return request<any>('/audits/schedules', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAuditSchedule(id: string, data: Partial<{
  scheduleName: string;
  frequency: string;
  nextDueDate: string;
  lastAuditDate: string;
  lastAuditId: string;
  isActive: boolean;
  riskRating: string;
  priority: number;
}>) {
  return request<any>(`/audits/schedules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Auditor Qualifications
export async function getAuditorQualifications(params?: { status?: string; auditorType?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.status) searchParams.set('status', params.status);
  if (params?.auditorType) searchParams.set('auditorType', params.auditorType);
  return request<{ data: any[] }>(`/audits/auditors?${searchParams}`);
}

export async function getAuditorQualification(id: string) {
  return request<any>(`/audits/auditors/${id}`);
}

export async function createAuditorQualification(data: {
  userId: string;
  auditorName: string;
  auditorType: string;
  qualificationStatus?: string;
  qualifications?: any[];
  areasOfExpertise?: string[];
  departmentsQualified?: string[];
  processesQualified?: string[];
  standardsQualified?: string[];
  trainingCompleted?: any[];
  totalTrainingHours?: number;
  effectiveDate?: string;
  expiryDate?: string;
}) {
  return request<any>('/audits/auditors', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAuditorQualification(id: string, data: Partial<{
  auditorType: string;
  qualificationStatus: string;
  qualifications: any[];
  areasOfExpertise: string[];
  standardsQualified: string[];
  trainingCompleted: any[];
  totalTrainingHours: number;
  auditsAsLead: number;
  auditsAsTeamMember: number;
  totalAuditDays: number;
  performanceRating: number;
  lastEvaluationDate: string;
  evaluationNotes: string;
  effectiveDate: string;
  expiryDate: string;
  suspendedDate: string;
  suspensionReason: string;
}>) {
  return request<any>(`/audits/auditors/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Audit Action Items
export async function getAuditActionItems(params?: PaginationParams & {
  auditId?: string;
  findingId?: string;
  status?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.auditId) searchParams.set('auditId', params.auditId);
  if (params?.findingId) searchParams.set('findingId', params.findingId);
  if (params?.status) searchParams.set('status', params.status);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/audits/actions?${searchParams}`);
}

export async function createAuditActionItem(data: {
  auditId: string;
  findingId?: string;
  actionType: string;
  actionDescription: string;
  priority?: string;
  assignedTo?: string;
  assignedDepartment?: string;
  assignedDate?: string;
  dueDate?: string;
  verificationRequired?: boolean;
  verificationMethod?: string;
}) {
  return request<any>('/audits/actions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAuditActionItem(id: string, data: Partial<{
  actionStatus: string;
  startedDate: string;
  completedDate: string;
  completionNotes: string;
  completedBy: string;
  extensionDate: string;
  extensionReason: string;
  verifiedBy: string;
  verifiedDate: string;
  verificationResult: string;
  verificationNotes: string;
}>) {
  return request<any>(`/audits/actions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Audit Templates
export async function getAuditTemplates(params?: { templateType?: string; auditCategory?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.templateType) searchParams.set('templateType', params.templateType);
  if (params?.auditCategory) searchParams.set('auditCategory', params.auditCategory);
  return request<{ data: any[] }>(`/audits/templates?${searchParams}`);
}

export async function createAuditTemplate(data: {
  templateName: string;
  templateType: string;
  auditCategory?: string;
  description?: string;
  contentTemplate?: string;
  placeholders?: string[];
  defaultChecklistId?: string;
  defaultDurationDays?: number;
  defaultTeamSize?: number;
  isDefault?: boolean;
}) {
  return request<any>('/audits/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateAuditTemplate(id: string, data: Partial<{
  templateName: string;
  description: string;
  contentTemplate: string;
  placeholders: string[];
  defaultChecklistId: string;
  isDefault: boolean;
  isActive: boolean;
}>) {
  return request<any>(`/audits/templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Audit Analytics
export async function getAuditAnalytics(params?: { period?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.period) searchParams.set('period', params.period);
  return request<{ data: any[] }>(`/audits/analytics?${searchParams}`);
}

// Audit Dashboard
export async function getAuditDashboard() {
  return request<{
    summary: {
      openAudits: number;
      newAudits30Days: number;
      openFindings: number;
      overdueActions: number;
      upcomingAudits: number;
    };
    recentAudits: any[];
    pendingActions: any[];
  }>('/audits/dashboard');
}

// ============================================================================
// PHASE 54: DOCUMENT CONTROL
// ============================================================================

// Document Categories
export async function getDocumentCategories(params?: { isActive?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
  return request<{ data: any[] }>(`/document-control/categories?${searchParams}`);
}

export async function getDocumentCategory(id: string) {
  return request<any>(`/document-control/categories/${id}`);
}

export async function createDocumentCategory(data: {
  categoryCode: string;
  categoryName: string;
  parentCategoryId?: string;
  description?: string;
  documentPrefix?: string;
  numberingFormat?: string;
  retentionYears?: number;
  reviewFrequencyMonths?: number;
  requiresTraining?: boolean;
  requiresEsignature?: boolean;
  approvalLevels?: number;
}) {
  return request<any>('/document-control/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDocumentCategory(id: string, data: Partial<{
  categoryName: string;
  description: string;
  documentPrefix: string;
  retentionYears: number;
  reviewFrequencyMonths: number;
  requiresTraining: boolean;
  approvalLevels: number;
  isActive: boolean;
}>) {
  return request<any>(`/document-control/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Document Control Types
export async function getDocControlTypes(params?: { categoryId?: string; isActive?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
  if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
  return request<{ data: any[] }>(`/document-control/types?${searchParams}`);
}

export async function getDocumentType(id: string) {
  return request<any>(`/document-control/types/${id}`);
}

export async function createDocumentType(data: {
  typeCode: string;
  typeName: string;
  categoryId?: string;
  description?: string;
  templateRequired?: boolean;
  defaultTemplateId?: string;
  standardSections?: any[];
  requiredApprovers?: any[];
  defaultDistribution?: any[];
}) {
  return request<any>('/document-control/types', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDocumentType(id: string, data: Partial<{
  typeName: string;
  description: string;
  standardSections: any[];
  requiredApprovers: any[];
  isActive: boolean;
}>) {
  return request<any>(`/document-control/types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Controlled Documents
export async function getControlledDocuments(params?: PaginationParams & {
  status?: string;
  categoryId?: string;
  typeId?: string;
  department?: string;
  search?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
  if (params?.typeId) searchParams.set('typeId', params.typeId);
  if (params?.department) searchParams.set('department', params.department);
  if (params?.search) searchParams.set('search', params.search);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/document-control?${searchParams}`);
}

export async function getControlledDocument(id: string) {
  return request<any>(`/document-control/${id}`);
}

export async function createControlledDocument(data: {
  documentTitle: string;
  documentNumber?: string;
  categoryId?: string;
  documentTypeId?: string;
  classification?: string;
  department?: string;
  processArea?: string;
  regulatoryReference?: string;
  standardReference?: string;
  documentOwnerId?: string;
  authorId?: string;
  custodianId?: string;
  purpose?: string;
  scope?: string;
  keywords?: string[];
  relatedDocuments?: string[];
  supersedesDocumentId?: string;
  trainingRequired?: boolean;
  isTemplate?: boolean;
  isControlled?: boolean;
  contentType?: string;
  content?: string;
  filePath?: string;
  fileName?: string;
  fileSize?: number;
}) {
  return request<any>('/document-control', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateControlledDocument(id: string, data: Partial<{
  documentTitle: string;
  documentStatus: string;
  classification: string;
  department: string;
  processArea: string;
  documentOwnerId: string;
  purpose: string;
  scope: string;
  keywords: string[];
  relatedDocuments: string[];
  trainingRequired: boolean;
  effectiveDate: string;
  expiryDate: string;
  nextReviewDate: string;
  isActive: boolean;
}>) {
  return request<any>(`/document-control/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteControlledDocument(id: string) {
  return request<any>(`/document-control/${id}`, { method: 'DELETE' });
}

// Document Versions
export async function getDocumentVersions(documentId: string, params?: { status?: string }) {
  const searchParams = new URLSearchParams();
  searchParams.set('documentId', documentId);
  if (params?.status) searchParams.set('status', params.status);
  return request<{ data: any[] }>(`/document-control/versions?${searchParams}`);
}

export async function getDocumentVersion(id: string) {
  return request<any>(`/document-control/versions/${id}`);
}

export async function createDocumentVersion(data: {
  documentId: string;
  changeType?: string;
  changeSummary?: string;
  changeReason?: string;
  changeRequestId?: string;
  sectionsChanged?: string[];
  contentType?: string;
  content?: string;
  filePath?: string;
  fileName?: string;
  fileSize?: number;
  fileHash?: string;
}) {
  return request<any>('/document-control/versions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDocumentVersion(id: string, data: Partial<{
  versionStatus: string;
  content: string;
  changeSummary: string;
  effectiveDate: string;
  supersededDate: string;
  obsoleteDate: string;
}>) {
  return request<any>(`/document-control/versions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Document Reviews
export async function getDocumentReviews(params?: PaginationParams & {
  documentId?: string;
  versionId?: string;
  status?: string;
  reviewerId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.documentId) searchParams.set('documentId', params.documentId);
  if (params?.versionId) searchParams.set('versionId', params.versionId);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.reviewerId) searchParams.set('reviewerId', params.reviewerId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/document-control/reviews?${searchParams}`);
}

export async function createDocumentReview(data: {
  documentId: string;
  versionId: string;
  reviewType: string;
  reviewerId?: string;
  reviewerName?: string;
  reviewerRole?: string;
  dueDate?: string;
}) {
  return request<any>('/document-control/reviews', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDocumentReview(id: string, data: Partial<{
  reviewStatus: string;
  startedDate: string;
  completedDate: string;
  reviewOutcome: string;
  comments: string;
  suggestedChanges: any[];
  checklistCompleted: boolean;
  checklistResponses: any[];
  signatureId: string;
  signedAt: string;
}>) {
  return request<any>(`/document-control/reviews/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Document Approvals
export async function getDocumentApprovals(params?: PaginationParams & {
  documentId?: string;
  versionId?: string;
  status?: string;
  approverId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.documentId) searchParams.set('documentId', params.documentId);
  if (params?.versionId) searchParams.set('versionId', params.versionId);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.approverId) searchParams.set('approverId', params.approverId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/document-control/approvals?${searchParams}`);
}

export async function createDocumentApproval(data: {
  documentId: string;
  versionId: string;
  approvalLevel: number;
  approvalSequence: number;
  approverId?: string;
  approverName?: string;
  approverRole?: string;
  approverDepartment?: string;
  dueDate?: string;
}) {
  return request<any>('/document-control/approvals', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDocumentApproval(id: string, data: Partial<{
  approvalStatus: string;
  decision: string;
  comments: string;
  conditions: any[];
  signatureId: string;
  signedAt: string;
  delegatedFromId: string;
  delegationReason: string;
}>) {
  return request<any>(`/document-control/approvals/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Document Distribution
export async function getDocumentDistribution(params?: PaginationParams & {
  documentId?: string;
  status?: string;
  recipientId?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.documentId) searchParams.set('documentId', params.documentId);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.recipientId) searchParams.set('recipientId', params.recipientId);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/document-control/distribution?${searchParams}`);
}

export async function createDocumentDistribution(data: {
  documentId: string;
  versionId: string;
  distributionType: string;
  recipientType: string;
  recipientId?: string;
  recipientName?: string;
  recipientEmail?: string;
  recipientDepartment?: string;
  distributionMethod?: string;
  copyType?: string;
  copyNumber?: string;
  acknowledgementRequired?: boolean;
  trainingRequired?: boolean;
}) {
  return request<any>('/document-control/distribution', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDocumentDistribution(id: string, data: Partial<{
  distributionStatus: string;
  distributedDate: string;
  acknowledgedDate: string;
  acknowledgementNotes: string;
  recalledDate: string;
  recallReason: string;
  recallConfirmed: boolean;
  trainingCompleted: boolean;
  trainingCompletedDate: string;
}>) {
  return request<any>(`/document-control/distribution/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Document Change Requests
export async function getDocumentChangeRequests(params?: PaginationParams & {
  documentId?: string;
  status?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.documentId) searchParams.set('documentId', params.documentId);
  if (params?.status) searchParams.set('status', params.status);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/document-control/change-requests?${searchParams}`);
}

export async function createDocumentChangeRequest(data: {
  documentId: string;
  currentVersionId?: string;
  requestType: string;
  urgency?: string;
  changeDescription: string;
  changeJustification?: string;
  sectionsAffected?: string[];
  impactAssessment?: string;
  targetDate?: string;
}) {
  return request<any>('/document-control/change-requests', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDocumentChangeRequest(id: string, data: Partial<{
  requestStatus: string;
  reviewedBy: string;
  reviewedDate: string;
  reviewComments: string;
  approvedBy: string;
  approvedDate: string;
  approvalComments: string;
  implementedBy: string;
  implementedDate: string;
  newVersionId: string;
  actualCompletionDate: string;
}>) {
  return request<any>(`/document-control/change-requests/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Document Templates
export async function getDocumentTemplates(params?: { categoryId?: string; typeId?: string; isActive?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
  if (params?.typeId) searchParams.set('typeId', params.typeId);
  if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
  return request<{ data: any[] }>(`/document-control/templates?${searchParams}`);
}

export async function createDocumentTemplate(data: {
  templateName: string;
  templateFormat: string;
  templateCode?: string;
  categoryId?: string;
  documentTypeId?: string;
  description?: string;
  templateContent?: string;
  templateFilePath?: string;
  templateSections?: any[];
  placeholders?: string[];
  version?: string;
  isDefault?: boolean;
}) {
  return request<any>('/document-control/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDocumentTemplate(id: string, data: Partial<{
  templateName: string;
  description: string;
  templateContent: string;
  templateSections: any[];
  placeholders: string[];
  version: string;
  isDefault: boolean;
  isActive: boolean;
}>) {
  return request<any>(`/document-control/templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Document Training
export async function getDocumentTraining(params?: PaginationParams & {
  documentId?: string;
  traineeId?: string;
  status?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.documentId) searchParams.set('documentId', params.documentId);
  if (params?.traineeId) searchParams.set('traineeId', params.traineeId);
  if (params?.status) searchParams.set('status', params.status);
  return request<{ data: any[]; total: number; page: number; pageSize: number }>(`/document-control/training?${searchParams}`);
}

export async function createDocumentTraining(data: {
  documentId: string;
  versionId: string;
  traineeId: string;
  traineeName: string;
  traineeDepartment?: string;
  trainingType: string;
  dueDate?: string;
  assessmentRequired?: boolean;
  passingScore?: number;
  trainerId?: string;
  trainerName?: string;
}) {
  return request<any>('/document-control/training', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDocumentTraining(id: string, data: Partial<{
  trainingStatus: string;
  startedDate: string;
  completedDate: string;
  assessmentScore: number;
  assessmentAttempts: number;
  acknowledged: boolean;
  acknowledgementText: string;
  acknowledgedDate: string;
  signatureId: string;
  notes: string;
}>) {
  return request<any>(`/document-control/training/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Document Review Schedules
export async function getDocumentReviewSchedules(params?: { documentId?: string; isActive?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.documentId) searchParams.set('documentId', params.documentId);
  if (params?.isActive !== undefined) searchParams.set('isActive', params.isActive.toString());
  return request<{ data: any[] }>(`/document-control/review-schedules?${searchParams}`);
}

export async function createDocumentReviewSchedule(data: {
  documentId: string;
  scheduleType: string;
  frequencyMonths?: number;
  triggerEvent?: string;
  nextReviewDate?: string;
  reminderDaysBefore?: number;
  defaultReviewerId?: string;
  defaultReviewerRole?: string;
}) {
  return request<any>('/document-control/review-schedules', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateDocumentReviewSchedule(id: string, data: Partial<{
  frequencyMonths: number;
  nextReviewDate: string;
  reminderDaysBefore: number;
  defaultReviewerId: string;
  isActive: boolean;
  lastReviewDate: string;
  lastReviewId: string;
}>) {
  return request<any>(`/document-control/review-schedules/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Document Analytics
export async function getDocumentControlAnalytics(params?: { period?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.period) searchParams.set('period', params.period);
  return request<{ data: any[] }>(`/document-control/analytics?${searchParams}`);
}

// Document Control Dashboard
export async function getDocumentControlDashboard() {
  return request<{
    summary: {
      totalDocuments: number;
      effectiveDocuments: number;
      pendingReviews: number;
      pendingApprovals: number;
      pendingTraining: number;
      overdueReviews: number;
    };
    recentDocuments: any[];
    pendingApprovalsList: any[];
  }>('/document-control/dashboard');
}

// =====================
// Phase 55: CAPA Management
// =====================

// CAPA Categories
export async function getCapaCategories() {
  return request<any[]>('/capa/categories');
}

export async function createCapaCategory(data: any) {
  return request<any>('/capa/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCapaCategory(id: string, data: any) {
  return request<any>(`/capa/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCapaCategory(id: string) {
  return request<{ success: boolean }>(`/capa/categories/${id}`, {
    method: 'DELETE',
  });
}

// CAPA Sources
export async function getCapaSources() {
  return request<any[]>('/capa/sources');
}

export async function createCapaSource(data: any) {
  return request<any>('/capa/sources', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCapaSource(id: string, data: any) {
  return request<any>(`/capa/sources/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCapaSource(id: string) {
  return request<{ success: boolean }>(`/capa/sources/${id}`, {
    method: 'DELETE',
  });
}

// CAPAs (main)
export async function getCapas(params?: PaginationParams & {
  search?: string;
  status?: string;
  type?: string;
  priority?: string;
  categoryId?: string;
  assignedTo?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params?.search) searchParams.set('search', params.search);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.type) searchParams.set('type', params.type);
  if (params?.priority) searchParams.set('priority', params.priority);
  if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
  if (params?.assignedTo) searchParams.set('assignedTo', params.assignedTo);
  return request<PaginatedResponse<any>>(`/capa?${searchParams}`);
}

export async function getCapa(id: string) {
  return request<any>(`/capa/${id}`);
}

export async function createCapa(data: any) {
  return request<any>('/capa', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCapa(id: string, data: any) {
  return request<any>(`/capa/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCapa(id: string) {
  return request<{ success: boolean }>(`/capa/${id}`, {
    method: 'DELETE',
  });
}

// CAPA Root Causes
export async function getCapaRootCauses(capaId: string) {
  return request<any[]>(`/capa/${capaId}/root-causes`);
}

export async function createCapaRootCause(capaId: string, data: any) {
  return request<any>(`/capa/${capaId}/root-causes`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCapaRootCause(capaId: string, id: string, data: any) {
  return request<any>(`/capa/${capaId}/root-causes/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCapaRootCause(capaId: string, id: string) {
  return request<{ success: boolean }>(`/capa/${capaId}/root-causes/${id}`, {
    method: 'DELETE',
  });
}

// CAPA Actions (Nested under CAPA ID)
export async function getCapaActionsById(capaId: string) {
  return request<any[]>(`/capa/${capaId}/actions`);
}

export async function createCapaActionById(capaId: string, data: any) {
  return request<any>(`/capa/${capaId}/actions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCapaActionById(capaId: string, id: string, data: any) {
  return request<any>(`/capa/${capaId}/actions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCapaActionById(capaId: string, id: string) {
  return request<{ success: boolean }>(`/capa/${capaId}/actions/${id}`, {
    method: 'DELETE',
  });
}

// CAPA Effectiveness Checks
export async function getCapaEffectiveness(capaId: string) {
  return request<any[]>(`/capa/${capaId}/effectiveness`);
}

export async function createCapaEffectiveness(capaId: string, data: any) {
  return request<any>(`/capa/${capaId}/effectiveness`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCapaEffectiveness(capaId: string, id: string, data: any) {
  return request<any>(`/capa/${capaId}/effectiveness/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// CAPA Attachments
export async function getCapaAttachments(capaId: string) {
  return request<any[]>(`/capa/${capaId}/attachments`);
}

export async function createCapaAttachment(capaId: string, data: any) {
  return request<any>(`/capa/${capaId}/attachments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteCapaAttachment(capaId: string, id: string) {
  return request<{ success: boolean }>(`/capa/${capaId}/attachments/${id}`, {
    method: 'DELETE',
  });
}

// CAPA Comments
export async function getCapaComments(capaId: string) {
  return request<any[]>(`/capa/${capaId}/comments`);
}

export async function createCapaComment(capaId: string, data: any) {
  return request<any>(`/capa/${capaId}/comments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// CAPA History
export async function getCapaHistory(capaId: string) {
  return request<any[]>(`/capa/${capaId}/history`);
}

// CAPA Reviews
export async function getCapaReviews(capaId: string) {
  return request<any[]>(`/capa/${capaId}/reviews`);
}

export async function createCapaReview(capaId: string, data: any) {
  return request<any>(`/capa/${capaId}/reviews`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCapaReview(capaId: string, id: string, data: any) {
  return request<any>(`/capa/${capaId}/reviews/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// CAPA Templates
export async function getCapaTemplates() {
  return request<any[]>('/capa/templates');
}

export async function createCapaTemplate(data: any) {
  return request<any>('/capa/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCapaTemplate(id: string, data: any) {
  return request<any>(`/capa/templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCapaTemplate(id: string) {
  return request<{ success: boolean }>(`/capa/templates/${id}`, {
    method: 'DELETE',
  });
}

// CAPA Notifications
export async function getCapaNotifications(params?: { unreadOnly?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.unreadOnly) searchParams.set('unreadOnly', 'true');
  return request<any[]>(`/capa/notifications?${searchParams}`);
}

export async function markCapaNotificationRead(id: string) {
  return request<any>(`/capa/notifications/${id}/read`, {
    method: 'PUT',
  });
}

// CAPA Analytics & Metrics (Standalone)
export async function getCapaAnalyticsStandalone() {
  return request<any[]>('/capa/analytics');
}

export async function generateCapaAnalytics(data: { periodStart: string; periodEnd: string }) {
  return request<any>('/capa/analytics/generate', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// CAPA Dashboard
export async function getCapaDashboard() {
  return request<{
    summary: {
      totalCapas: number;
      openCapas: number;
      overdueCapas: number;
      dueThisWeek: number;
      pendingActions: number;
    };
    statusBreakdown: Record<string, number>;
    typeBreakdown: Record<string, number>;
    priorityBreakdown: Record<string, number>;
    recentCapas: any[];
  }>('/capa/dashboard');
}

// =====================
// Phase 56: NCR (Non-Conformance Report) Management
// =====================

// NCR Categories
export async function getNcrCategories() {
  return request<any[]>('/ncr/categories');
}

export async function createNcrCategory(data: any) {
  return request<any>('/ncr/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateNcrCategory(id: string, data: any) {
  return request<any>(`/ncr/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteNcrCategory(id: string) {
  return request<{ success: boolean }>(`/ncr/categories/${id}`, {
    method: 'DELETE',
  });
}

// NCR Types
export async function getNcrTypes(params?: { categoryId?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
  return request<any[]>(`/ncr/types?${searchParams}`);
}

export async function createNcrType(data: any) {
  return request<any>('/ncr/types', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateNcrType(id: string, data: any) {
  return request<any>(`/ncr/types/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteNcrType(id: string) {
  return request<{ success: boolean }>(`/ncr/types/${id}`, {
    method: 'DELETE',
  });
}

// NCRs (main)
export async function getNcrs(params?: PaginationParams & {
  search?: string;
  status?: string;
  severity?: string;
  categoryId?: string;
  typeId?: string;
  supplierId?: string;
  assignedTo?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params?.search) searchParams.set('search', params.search);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.severity) searchParams.set('severity', params.severity);
  if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
  if (params?.typeId) searchParams.set('typeId', params.typeId);
  if (params?.supplierId) searchParams.set('supplierId', params.supplierId);
  if (params?.assignedTo) searchParams.set('assignedTo', params.assignedTo);
  return request<PaginatedResponse<any>>(`/ncr?${searchParams}`);
}

export async function getNcr(id: string) {
  return request<any>(`/ncr/${id}`);
}

export async function createNcr(data: any) {
  return request<any>('/ncr', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateNcr(id: string, data: any) {
  return request<any>(`/ncr/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteNcr(id: string) {
  return request<{ success: boolean }>(`/ncr/${id}`, {
    method: 'DELETE',
  });
}

// NCR Investigations
export async function getNcrInvestigations(ncrId: string) {
  return request<any[]>(`/ncr/${ncrId}/investigations`);
}

export async function createNcrInvestigation(ncrId: string, data: any) {
  return request<any>(`/ncr/${ncrId}/investigations`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateNcrInvestigation(ncrId: string, id: string, data: any) {
  return request<any>(`/ncr/${ncrId}/investigations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// NCR Containment
export async function getNcrContainment(ncrId: string) {
  return request<any[]>(`/ncr/${ncrId}/containment`);
}

export async function createNcrContainment(ncrId: string, data: any) {
  return request<any>(`/ncr/${ncrId}/containment`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateNcrContainment(ncrId: string, id: string, data: any) {
  return request<any>(`/ncr/${ncrId}/containment/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// NCR Corrective Actions
export async function getNcrCorrectiveActions(ncrId: string) {
  return request<any[]>(`/ncr/${ncrId}/corrective-actions`);
}

export async function createNcrCorrectiveAction(ncrId: string, data: any) {
  return request<any>(`/ncr/${ncrId}/corrective-actions`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateNcrCorrectiveAction(ncrId: string, id: string, data: any) {
  return request<any>(`/ncr/${ncrId}/corrective-actions/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteNcrCorrectiveAction(ncrId: string, id: string) {
  return request<{ success: boolean }>(`/ncr/${ncrId}/corrective-actions/${id}`, {
    method: 'DELETE',
  });
}

// NCR Approvals
export async function getNcrApprovals(ncrId: string) {
  return request<any[]>(`/ncr/${ncrId}/approvals`);
}

export async function createNcrApproval(ncrId: string, data: any) {
  return request<any>(`/ncr/${ncrId}/approvals`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateNcrApproval(ncrId: string, id: string, data: any) {
  return request<any>(`/ncr/${ncrId}/approvals/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// NCR Attachments
export async function getNcrAttachments(ncrId: string) {
  return request<any[]>(`/ncr/${ncrId}/attachments`);
}

export async function createNcrAttachment(ncrId: string, data: any) {
  return request<any>(`/ncr/${ncrId}/attachments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteNcrAttachment(ncrId: string, id: string) {
  return request<{ success: boolean }>(`/ncr/${ncrId}/attachments/${id}`, {
    method: 'DELETE',
  });
}

// NCR Comments
export async function getNcrComments(ncrId: string) {
  return request<any[]>(`/ncr/${ncrId}/comments`);
}

export async function createNcrComment(ncrId: string, data: any) {
  return request<any>(`/ncr/${ncrId}/comments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// NCR History
export async function getNcrHistory(ncrId: string) {
  return request<any[]>(`/ncr/${ncrId}/history`);
}

// NCR Templates
export async function getNcrTemplates() {
  return request<any[]>('/ncr/templates');
}

export async function createNcrTemplate(data: any) {
  return request<any>('/ncr/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateNcrTemplate(id: string, data: any) {
  return request<any>(`/ncr/templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteNcrTemplate(id: string) {
  return request<{ success: boolean }>(`/ncr/templates/${id}`, {
    method: 'DELETE',
  });
}

// NCR Notifications
export async function getNcrNotifications(params?: { unreadOnly?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.unreadOnly) searchParams.set('unreadOnly', 'true');
  return request<any[]>(`/ncr/notifications?${searchParams}`);
}

export async function markNcrNotificationRead(id: string) {
  return request<any>(`/ncr/notifications/${id}/read`, {
    method: 'PUT',
  });
}

// NCR Analytics
export async function getNcrAnalytics() {
  return request<any[]>('/ncr/analytics');
}

export async function generateNcrAnalytics(data: { periodStart: string; periodEnd: string }) {
  return request<any>('/ncr/analytics/generate', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// NCR Dashboard
export async function getNcrDashboard() {
  return request<{
    summary: {
      totalNcrs: number;
      openNcrs: number;
      overdueNcrs: number;
      quarantinedNcrs: number;
      pendingApprovals: number;
    };
    statusBreakdown: Record<string, number>;
    severityBreakdown: Record<string, number>;
    dispositionBreakdown: Record<string, number>;
    recentNcrs: any[];
  }>('/ncr/dashboard');
}

// ============================================================================
// PHASE 57: OOS/OOT MANAGEMENT
// ============================================================================

// OOS Categories
export async function getOosCategories(params?: { type?: string; active?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.type) searchParams.set('type', params.type);
  if (params?.active !== undefined) searchParams.set('active', String(params.active));
  return request<any[]>(`/oos/categories?${searchParams}`);
}

export async function getOosCategory(id: string) {
  return request<any>(`/oos/categories/${id}`);
}

export async function createOosCategory(data: any) {
  return request<any>('/oos/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOosCategory(id: string, data: any) {
  return request<any>(`/oos/categories/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteOosCategory(id: string) {
  return request<{ success: boolean }>(`/oos/categories/${id}`, {
    method: 'DELETE',
  });
}

// OOS Test Methods
export async function getOosTestMethods(params?: { testType?: string; active?: boolean; search?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.testType) searchParams.set('testType', params.testType);
  if (params?.active !== undefined) searchParams.set('active', String(params.active));
  if (params?.search) searchParams.set('search', params.search);
  return request<any[]>(`/oos/test-methods?${searchParams}`);
}

export async function getOosTestMethod(id: string) {
  return request<any>(`/oos/test-methods/${id}`);
}

export async function createOosTestMethod(data: any) {
  return request<any>('/oos/test-methods', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOosTestMethod(id: string, data: any) {
  return request<any>(`/oos/test-methods/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteOosTestMethod(id: string) {
  return request<{ success: boolean }>(`/oos/test-methods/${id}`, {
    method: 'DELETE',
  });
}

// OOS Records
export async function getOosRecords(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  recordType?: string;
  status?: string;
  priority?: string;
  categoryId?: string;
  testMethodId?: string;
  batchNumber?: string;
  assignedTo?: string;
  startDate?: string;
  endDate?: string;
}) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', String(params.page));
  if (params?.pageSize) searchParams.set('pageSize', String(params.pageSize));
  if (params?.search) searchParams.set('search', params.search);
  if (params?.recordType) searchParams.set('recordType', params.recordType);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.priority) searchParams.set('priority', params.priority);
  if (params?.categoryId) searchParams.set('categoryId', params.categoryId);
  if (params?.testMethodId) searchParams.set('testMethodId', params.testMethodId);
  if (params?.batchNumber) searchParams.set('batchNumber', params.batchNumber);
  if (params?.assignedTo) searchParams.set('assignedTo', params.assignedTo);
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  return request<{ data: any[]; pagination: any }>(`/oos?${searchParams}`);
}

export async function getOosRecord(id: string) {
  return request<any>(`/oos/${id}`);
}

export async function createOosRecord(data: any) {
  return request<any>('/oos', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOosRecord(id: string, data: any) {
  return request<any>(`/oos/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteOosRecord(id: string) {
  return request<{ success: boolean }>(`/oos/${id}`, {
    method: 'DELETE',
  });
}

// OOS Phase 1 Checklist
export async function getOosPhase1Checklist(oosId: string) {
  return request<any[]>(`/oos/${oosId}/phase1-checklist`);
}

export async function addOosPhase1ChecklistItem(oosId: string, data: any) {
  return request<any>(`/oos/${oosId}/phase1-checklist`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOosPhase1ChecklistItem(oosId: string, itemId: string, data: any) {
  return request<any>(`/oos/${oosId}/phase1-checklist/${itemId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteOosPhase1ChecklistItem(oosId: string, itemId: string) {
  return request<{ success: boolean }>(`/oos/${oosId}/phase1-checklist/${itemId}`, {
    method: 'DELETE',
  });
}

// OOS Investigations
export async function getOosInvestigations(oosId: string, params?: { phase?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.phase) searchParams.set('phase', params.phase);
  return request<any[]>(`/oos/${oosId}/investigations?${searchParams}`);
}

export async function createOosInvestigation(oosId: string, data: any) {
  return request<any>(`/oos/${oosId}/investigations`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOosInvestigation(oosId: string, investigationId: string, data: any) {
  return request<any>(`/oos/${oosId}/investigations/${investigationId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteOosInvestigation(oosId: string, investigationId: string) {
  return request<{ success: boolean }>(`/oos/${oosId}/investigations/${investigationId}`, {
    method: 'DELETE',
  });
}

// OOS Retests
export async function getOosRetests(oosId: string) {
  return request<any[]>(`/oos/${oosId}/retests`);
}

export async function createOosRetest(oosId: string, data: any) {
  return request<any>(`/oos/${oosId}/retests`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOosRetest(oosId: string, retestId: string, data: any) {
  return request<any>(`/oos/${oosId}/retests/${retestId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteOosRetest(oosId: string, retestId: string) {
  return request<{ success: boolean }>(`/oos/${oosId}/retests/${retestId}`, {
    method: 'DELETE',
  });
}

// OOS Approvals
export async function getOosApprovals(oosId: string) {
  return request<any[]>(`/oos/${oosId}/approvals`);
}

export async function requestOosApproval(oosId: string, data: any) {
  return request<any>(`/oos/${oosId}/approvals`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function processOosApproval(oosId: string, approvalId: string, data: any) {
  return request<any>(`/oos/${oosId}/approvals/${approvalId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// OOS Attachments
export async function getOosAttachments(oosId: string) {
  return request<any[]>(`/oos/${oosId}/attachments`);
}

export async function addOosAttachment(oosId: string, data: any) {
  return request<any>(`/oos/${oosId}/attachments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteOosAttachment(oosId: string, attachmentId: string) {
  return request<{ success: boolean }>(`/oos/${oosId}/attachments/${attachmentId}`, {
    method: 'DELETE',
  });
}

// OOS Comments
export async function getOosComments(oosId: string) {
  return request<any[]>(`/oos/${oosId}/comments`);
}

export async function addOosComment(oosId: string, data: any) {
  return request<any>(`/oos/${oosId}/comments`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteOosComment(oosId: string, commentId: string) {
  return request<{ success: boolean }>(`/oos/${oosId}/comments/${commentId}`, {
    method: 'DELETE',
  });
}

// OOS History
export async function getOosHistory(oosId: string) {
  return request<any[]>(`/oos/${oosId}/history`);
}

// OOS Templates
export async function getOosTemplates(params?: { recordType?: string; active?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.recordType) searchParams.set('recordType', params.recordType);
  if (params?.active !== undefined) searchParams.set('active', String(params.active));
  return request<any[]>(`/oos/templates?${searchParams}`);
}

export async function getOosTemplate(id: string) {
  return request<any>(`/oos/templates/${id}`);
}

export async function createOosTemplate(data: any) {
  return request<any>('/oos/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOosTemplate(id: string, data: any) {
  return request<any>(`/oos/templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteOosTemplate(id: string) {
  return request<{ success: boolean }>(`/oos/templates/${id}`, {
    method: 'DELETE',
  });
}

// OOS Notifications
export async function getOosNotifications(params?: { unreadOnly?: boolean }) {
  const searchParams = new URLSearchParams();
  if (params?.unreadOnly) searchParams.set('unreadOnly', 'true');
  return request<any[]>(`/oos/notifications?${searchParams}`);
}

export async function markOosNotificationRead(id: string) {
  return request<any>(`/oos/notifications/${id}/read`, {
    method: 'PUT',
  });
}

// OOS Dashboard
export async function getOosDashboard() {
  return request<{
    total: number;
    byStatus: Record<string, number>;
    byType: Record<string, number>;
    byPriority: Record<string, number>;
    recentRecords: any[];
    overdueCount: number;
  }>('/oos/dashboard');
}

// OOS Analytics
export async function getOosAnalytics(params?: { startDate?: string; endDate?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  return request<{
    total: number;
    oosCount: number;
    ootCount: number;
    byDisposition: Record<string, number>;
    byRootCause: Record<string, number>;
    avgClosureDays: number | null;
    labErrorRate: string;
  }>(`/oos/analytics?${searchParams}`);
}
